"use client"

import type { SelectedCcFilters, SelectedTagsFilter } from "@/modules/jobAds/client/hooks/useCcFilters"
import type { FunctionComponent, MouseEventHandler } from "react"

import { clsx } from "clsx"
import dynamic from "next/dynamic"
import Image from "next/image"
import { useState } from "react"

import { ChevronDown } from "@jobteaser/spark/components/Icons/ChevronDown"

import { useCcFilters } from "@/modules/jobAds/client/hooks/useCcFilters"
import { useJobAdsFilterNavigation } from "@/modules/jobAds/client/hooks/useJobAdsFilterNavigation"
import { FilterButton } from "@/modules/landingSearch/components/FilterButton/FilterButton"

import styles from "./CcFilters.module.css"

const DynamicCcFiltersDropdown = dynamic(() =>
  import("@/modules/jobAds/client/components/CcFiltersDropdown/CcFiltersDropdown").then(mod => mod.CcFiltersDropdown)
)

type CcFiltersProps = {
  className?: string
  label: string
  logoSrc: string | undefined
  schoolName: string
}

export const CcFilters: FunctionComponent<CcFiltersProps> = ({ className, label, logoSrc, schoolName }) => {
  const { navigateWithUpdatedQueryParams } = useJobAdsFilterNavigation()
  const { getCcParameters, getTagParameter, selectedCcFiltersNumber } = useCcFilters()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const hasSelectedFilters = selectedCcFiltersNumber > 0

  const handleApply = (newCcFilters: SelectedCcFilters, newTagsFilter: SelectedTagsFilter): void => {
    setIsDropdownOpen(false)
    navigateWithUpdatedQueryParams({
      availableFilters: { ...getCcParameters(newCcFilters), ...getTagParameter(newTagsFilter) },
    })
  }

  const handleClick: MouseEventHandler<HTMLButtonElement> = event => {
    event.stopPropagation()
    setIsDropdownOpen(!isDropdownOpen)
  }

  return (
    <div className={clsx(className, styles.main)}>
      <FilterButton
        className={isDropdownOpen ? styles.button___active : ""}
        counter={hasSelectedFilters ? selectedCcFiltersNumber : null}
        data-test-id="job_ads_cc_filter"
        isActive={hasSelectedFilters}
        onClick={handleClick}
      >
        {logoSrc && <Image alt="School logo" className={styles.logo} height={24} src={logoSrc} width={24} />}
        <span className={styles.label}>{label}</span>
        {!hasSelectedFilters && <ChevronDown />}
      </FilterButton>

      {isDropdownOpen && (
        <DynamicCcFiltersDropdown
          className={styles.dropdown}
          onApply={(newCcFilters, newTagsFilter) => handleApply(newCcFilters, newTagsFilter)}
          schoolName={schoolName}
        />
      )}
    </div>
  )
}
